/**
 * Upgrade modal — shown inline when a user hits their billing limit.
 * Displays current usage, reset date, and a one-click upgrade CTA.
 * Mirrors the email-verification-modal pattern (Zustand store + Dialog).
 */

import { Link } from "@tanstack/react-router";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { authClient } from "@/lib/api/auth-client";

// -- Store --

export interface UpgradeModalData {
	plan: string;
	runsUsed: number;
	runsLimit: number;
	periodEnd: string;
	errorCode?: string;
}

interface UpgradeModalState {
	isOpen: boolean;
	data: UpgradeModalData | null;
	open: (data: UpgradeModalData) => void;
	close: () => void;
}

export const useUpgradeModalStore = create<UpgradeModalState>((set) => ({
	isOpen: false,
	data: null,
	open: (data) => set({ isOpen: true, data }),
	close: () => set({ isOpen: false, data: null }),
}));

// -- Upgrade tier map --

interface UpgradeTarget {
	planId: string;
	name: string;
	price: number;
	runs: number;
}

export const UPGRADE_MAP: Record<string, UpgradeTarget> = {
	free: { planId: "pro", name: "Pro", price: 29, runs: 100 },
	pro: { planId: "max_5x", name: "Max 5x", price: 99, runs: 500 },
	max_5x: { planId: "max_20x", name: "Max 20x", price: 299, runs: 2000 },
};

export const PLAN_DISPLAY: Record<string, string> = {
	free: "Free",
	pro: "Pro",
	max_5x: "Max 5x",
	max_20x: "Max 20x",
};

/** Hook for one-click Stripe checkout. Returns { checkout, isLoading }. */
export function useUpgradeCheckout() {
	const [isLoading, setIsLoading] = useState(false);
	const checkout = async (planId: string) => {
		setIsLoading(true);
		const { data, error } = await authClient.POST("/api/v1/billing/checkout", {
			body: { plan_id: planId, billing_period: "monthly" },
		});
		if (data?.url) {
			window.location.href = data.url;
		} else {
			toast.error((error as { detail?: string })?.detail ?? "Checkout failed. Try again.");
			setIsLoading(false);
		}
	};
	return { checkout, isLoading };
}

function getDaysUntilReset(periodEnd: string): number {
	const diff = new Date(periodEnd).getTime() - Date.now();
	return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function formatResetDate(periodEnd: string): string {
	return new Date(periodEnd).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatResetDateTime(periodEnd: string): string {
	return new Date(periodEnd).toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

/** Open the upgrade modal from a 402 error response's extra dict (snake_case keys from backend). */
export function openUpgradeModalFromError(errorData?: Record<string, unknown>) {
	const { plan, runs_used, runs_limit, period_end, error_code } = (errorData ?? {}) as {
		plan?: string;
		runs_used?: number;
		runs_limit?: number;
		period_end?: string;
		error_code?: string;
	};
	if (plan && runs_limit != null && period_end) {
		useUpgradeModalStore.getState().open({
			plan,
			runsUsed: runs_used ?? runs_limit,
			runsLimit: runs_limit,
			periodEnd: period_end,
			errorCode: error_code,
		});
	}
}

// -- Component --

export function UpgradeModal() {
	const { isOpen, data, close } = useUpgradeModalStore();
	const { checkout, isLoading } = useUpgradeCheckout();

	if (!data) return null;

	const { plan, runsUsed, runsLimit, periodEnd, errorCode } = data;
	const daysUntilReset = getDaysUntilReset(periodEnd);
	const resetDate = formatResetDate(periodEnd);
	const planName = PLAN_DISPLAY[plan] ?? plan;
	const upgradeTarget = UPGRADE_MAP[plan];

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
			<DialogContent className="max-w-sm">
				<DialogHeader className="items-center text-center">
					<div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900">
						<TriangleAlert className="size-6 text-orange-600 dark:text-orange-300" />
					</div>
					<DialogTitle>
						{errorCode === "COST_LIMIT_REACHED"
							? "Fair usage limit reached"
							: "You've reached your limit"}
					</DialogTitle>
					<DialogDescription>
						{errorCode !== "COST_LIMIT_REACHED" && (
							<>
								{runsUsed}/{runsLimit} runs used &middot;{" "}
							</>
						)}
						{planName} &middot;{" "}
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="cursor-default">
									Resets in {daysUntilReset} {daysUntilReset === 1 ? "day" : "days"}
								</span>
							</TooltipTrigger>
							<TooltipContent variant="subtle">{formatResetDateTime(periodEnd)}</TooltipContent>
						</Tooltip>
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{/* Upgrade CTA or top-tier message */}
					{upgradeTarget ? (
						<div className="rounded-lg border bg-card p-4 space-y-3">
							<div className="flex items-center justify-between">
								<div>
									<p className="font-medium">{upgradeTarget.name}</p>
									<p className="text-sm text-muted-foreground">{upgradeTarget.runs} runs/month</p>
								</div>
								<p className="text-lg font-semibold">${upgradeTarget.price}/mo</p>
							</div>
							<Button
								className="w-full"
								onClick={() => upgradeTarget && checkout(upgradeTarget.planId)}
								disabled={isLoading}
							>
								{isLoading ? <Spinner className="size-4" /> : `Upgrade to ${upgradeTarget.name}`}
							</Button>
						</div>
					) : (
						<div className="rounded-lg border bg-card p-4 space-y-3 text-center">
							<p className="text-sm text-muted-foreground">
								You're on our highest plan. Your usage resets {resetDate}.
							</p>
							<Button variant="outline" className="w-full" asChild>
								<a href="mailto:sales@m8tes.ai">Contact sales for more</a>
							</Button>
						</div>
					)}

					{/* View all plans link */}
					<p className="text-center">
						<Link
							to="/billing"
							onClick={close}
							className="text-sm text-muted-foreground underline hover:no-underline"
						>
							View all plans
						</Link>
					</p>
				</div>
			</DialogContent>
		</Dialog>
	);
}
